import { createContext, useContext, useEffect, useState, useRef, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { ehAreaLogada, resolverHomeDoPerfil, type Papel } from '../lib/navegacao';

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  notification_preferences?: Record<string, boolean>;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  userRole: string | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signInWithEmail: (email: string, password: string, restrictedAccess?: boolean) => Promise<{ error: string | null }>;
  signUpWithEmail: (email: string, password: string, fullName: string) => Promise<{ error: string | null }>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  openAuthModal: () => void;
  closeAuthModal: () => void;
  isAuthModalOpen: boolean;
  fetchProfile: (userId: string) => Promise<void>;
  authError: string | null;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const profileCache = new Map<string, { profile: Profile; role: string | null; ts: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000;

// Marcador de intenção de login, guardado no sessionStorage para sobreviver
// ao redirecionamento do login social (Google/Facebook).
//   'email' = login por e-mail/senha (o signInWithEmail conduz trava + redirect)
//   'oauth' = login social (sem caixa "Acesso Restrito" → só usuário comum)
const ACCESS_INTENT_KEY = 'sb_access_intent';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const fetchingRef = useRef<Set<string>>(new Set());

  const clearAuthError = useCallback(() => setAuthError(null), []);

  const fetchProfile = useCallback(async (userId: string) => {
    if (fetchingRef.current.has(userId)) return;

    const cached = profileCache.get(userId);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      setProfile(cached.profile);
      setUserRole(cached.role);
      return;
    }

    fetchingRef.current.add(userId);
    try {
      const [{ data: profileData }, { data: roleData }] = await Promise.all([
        supabase.from('profiles').select('id, full_name, email, avatar_url, notification_preferences').eq('id', userId).maybeSingle(),
        supabase.from('user_roles').select('role').eq('user_id', userId).maybeSingle(),
      ]);

      const resolvedProfile = profileData ?? null;
      const resolvedRole = roleData?.role ?? null;

      if (resolvedProfile) setProfile(resolvedProfile);
      setUserRole(resolvedRole);

      if (resolvedProfile) {
        profileCache.set(userId, { profile: resolvedProfile, role: resolvedRole, ts: Date.now() });
      }
    } catch {
      // Ignorar erros de perfil
    } finally {
      fetchingRef.current.delete(userId);
    }
  }, []);

  useEffect(() => {
    // Inicializar sessão com tratamento de erros
    supabase.auth.getSession()
      .then(({ data: { session: s }, error }) => {
        if (error) {
          supabase.auth.signOut().catch(() => {});
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user) {
          fetchProfile(s.user.id).finally(() => setLoading(false));
        } else {
          setLoading(false);
        }
      })
      .catch(() => {
        setSession(null);
        setUser(null);
        setLoading(false);
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);

      if (s?.user) {
        setIsAuthModalOpen(false);

        if (_event === 'SIGNED_IN') {
          const intent = sessionStorage.getItem(ACCESS_INTENT_KEY);

          // ── Login por E-MAIL/SENHA ─────────────────────────────────────────
          // O próprio signInWithEmail() aplica a trava de "Acesso Restrito" e
          // faz o redirecionamento conforme o papel. Aqui não fazemos nada além
          // de carregar o perfil, para não causar redirecionamento duplicado.
          if (intent === 'email') {
            fetchProfile(s.user!.id);
            return;
          }

          // ── Login SOCIAL (Google/Facebook/Apple) ───────────────────────────
          // O login social NÃO tem a caixa "Acesso Restrito". Pela regra do
          // sistema, essa porta é exclusiva para usuário comum. Contas de
          // admin/instrutor são bloqueadas aqui.
          //
          // ┌─────────────────────────────────────────────────────────────────┐
          // │ BLOCO REMOVÍVEL — bloqueio do login social para admin/instrutor. │
          // │ Se você quiser permitir admin/instrutor pelo Google, apague deste │
          // │ "if (intent === 'oauth')" até o "return;" correspondente e troque │
          // │ pela versão simples comentada logo abaixo.                        │
          // └─────────────────────────────────────────────────────────────────┘
          if (intent === 'oauth') {
            sessionStorage.removeItem(ACCESS_INTENT_KEY);
            fetchProfile(s.user!.id).then(() => {
              const role = profileCache.get(s.user!.id)?.role ?? null;
              const isPrivileged = role === 'admin' || role === 'instructor';
              if (isPrivileged) {
                // Derruba a sessão recém-criada e avisa o usuário.
                supabase.auth.signOut().then(() => {
                  setAuthError(
                    'Contas de administrador ou instrutor não entram pelo login social. ' +
                    'Use e-mail e senha e marque "Acesso Restrito".'
                  );
                  setIsAuthModalOpen(true);
                });
                return;
              }
              // Usuário comum → vai para a casa do SEU perfil (mentorado cai na
              // trilha, aluno no dashboard). Quem decide é homeDoPerfil.
              resolverHomeDoPerfil(role as Papel).then((destino) => {
                (window as any).REACT_APP_NAVIGATE?.(destino);
              });
            });
            return;
          }
          // Versão simples (login social liberado para todos), caso você remova o bloco acima:
          // if (intent === 'oauth') {
          //   sessionStorage.removeItem(ACCESS_INTENT_KEY);
          //   fetchProfile(s.user!.id).then(() => {
          //     const role = (profileCache.get(s.user!.id)?.role ?? null) as Papel;
          //     resolverHomeDoPerfil(role).then((destino) => {
          //       (window as any).REACT_APP_NAVIGATE?.(destino);
          //     });
          //   });
          //   return;
          // }

          // ── Sem marcador ───────────────────────────────────────────────────
          // Recarregar página, restaurar sessão, ou login disparado em outra
          // aba. NÃO é um login deliberado pelos nossos botões, então mantemos o
          // comportamento antigo (redireciona por papel) e NÃO bloqueamos quem
          // já está autenticado.
          const path = window.location.pathname;
          // A allow-list das rotas de ÁREA LOGADA mora em lib/navegacao.ts.
          // Estando numa delas, a pessoa já está onde quis estar: não mexemos.
          if (!ehAreaLogada(path)) {
            // Aguarda o perfil carregar completamente antes de redirecionar
            fetchProfile(s.user!.id).then(() => {
              const role = (profileCache.get(s.user!.id)?.role ?? null) as Papel;
              resolverHomeDoPerfil(role).then((destino) => {
                (window as any).REACT_APP_NAVIGATE?.(destino);
              });
            });
          }
        } else {
          fetchProfile(s.user!.id);
        }
      } else {
        setProfile(null);
        setUserRole(null);
        profileCache.clear();
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signInWithGoogle = async () => {
    // Marca a intenção como login social (sem caixa "Acesso Restrito").
    sessionStorage.setItem(ACCESS_INTENT_KEY, 'oauth');
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}${__BASE_PATH__}` },
    });
  };

  const signInWithFacebook = async () => {
    // Marca a intenção como login social (sem caixa "Acesso Restrito").
    sessionStorage.setItem(ACCESS_INTENT_KEY, 'oauth');
    await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: { redirectTo: `${window.location.origin}${__BASE_PATH__}` },
    });
  };

  const signInWithEmail = async (
    email: string,
    password: string,
    restrictedAccess: boolean = false,
  ): Promise<{ error: string | null }> => {
    // Marca que este login é por e-mail: o redirecionamento será feito AQUI,
    // e o onAuthStateChange não deve redirecionar por conta própria.
    sessionStorage.setItem(ACCESS_INTENT_KEY, 'email');

    // 1) Valida e-mail e senha no Supabase (autenticação real).
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      sessionStorage.removeItem(ACCESS_INTENT_KEY);
      if (error.message.includes('Invalid login credentials')) {
        return { error: 'E-mail ou senha incorretos. Verifique seus dados e tente novamente.' };
      }
      if (error.message.includes('Email not confirmed')) {
        return { error: 'Confirme seu e-mail antes de entrar. Verifique sua caixa de entrada.' };
      }
      return { error: error.message };
    }

    // 2) Senha correta → descobre o papel (role) deste usuário.
    const userId = data.user!.id;
    const { data: roleData } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle();
    const role = roleData?.role ?? null;
    const isPrivileged = role === 'admin' || role === 'instructor';

    // 3) TRAVA — a caixa "Acesso Restrito" tem que CASAR com o tipo de conta.
    //    Admin/instrutor SÓ entram com a caixa MARCADA.
    if (isPrivileged && !restrictedAccess) {
      await supabase.auth.signOut();                 // derruba a sessão recém-criada
      sessionStorage.removeItem(ACCESS_INTENT_KEY);
      return {
        error: 'Acesso de administrador/instrutor: marque "Acesso Restrito" para entrar no painel.',
      };
    }
    //    Usuário comum SÓ entra com a caixa DESMARCADA.
    if (!isPrivileged && restrictedAccess) {
      await supabase.auth.signOut();
      sessionStorage.removeItem(ACCESS_INTENT_KEY);
      return {
        error: '"Acesso Restrito" é exclusivo para administradores e instrutores. Desmarque a opção para entrar.',
      };
    }

    // 4) Passou na trava → vai para a casa do perfil. O instrutor NÃO cai mais
    //    na área de aluno: sua área ainda não existe, então vai para a home
    //    pública com aviso explícito (ver homeDoPerfil).
    const destino = await resolverHomeDoPerfil(role as Papel);
    (window as any).REACT_APP_NAVIGATE?.(destino);
    sessionStorage.removeItem(ACCESS_INTENT_KEY);
    return { error: null };
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    fullName: string,
  ): Promise<{ error: string | null }> => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}${__BASE_PATH__}`,
      },
    });
    if (error) {
      if (error.message.includes('already registered') || error.message.includes('already exists')) {
        return { error: 'Este e-mail já está cadastrado. Tente fazer login.' };
      }
      return { error: error.message };
    }
    if (data.user) {
      await supabase.from('profiles').upsert({
        id: data.user.id,
        email,
        full_name: fullName,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'id' });
    }
    return { error: null };
  };

  const resetPassword = async (email: string): Promise<{ error: string | null }> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${__BASE_PATH__}dashboard/perfil`,
    });
    if (error) return { error: error.message };
    return { error: null };
  };

  const signOut = async () => {
    profileCache.clear();
    await supabase.auth.signOut();
    (window as any).REACT_APP_NAVIGATE?.('/');
  };

  const openAuthModal = () => setIsAuthModalOpen(true);
  const closeAuthModal = () => setIsAuthModalOpen(false);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        userRole,
        loading,
        signInWithGoogle,
        signInWithFacebook,
        signInWithEmail,
        signUpWithEmail,
        resetPassword,
        signOut,
        openAuthModal,
        closeAuthModal,
        isAuthModalOpen,
        fetchProfile,
        authError,
        clearAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}